/**
 * Everything that used to be registered on the `Vue` global, as one app plugin.
 *
 * `Vue.component(...)`, `Vue.directive(...)`, `Vue.use(...)` and `Vue.prototype.$x = ...`
 * are vue 2's global API: one registry, and every root ever created reads from it. Vue 3
 * has no such object - registration is per app - and @vue/compat bridged the gap by copying
 * its singleton's registry into each app it built. That copy was the last thing standing
 * between this app and plain vue 3, and it is what GLOBAL_PROTOTYPE kept alive.
 *
 * So the registry is written out here instead, as an `install(app)` that vue/createApp.js
 * runs for every root: the five site wide mounts, the spa, and every detached popup
 * simplePopupFactory builds. Nothing changes for a component - the same names resolve, the
 * same `$notifications` and `$customTerm` are on `this` - but the app doing the resolving
 * now says where they came from.
 *
 * index.js is what registers this, rather than createApp importing it: the components below
 * reach simplePopupFactory, which imports createApp, and a module cycle through the one
 * place every root is built is not worth the tidiness.
 */
import Vue3TouchEvents from 'vue3-touch-events';

import infiniteScroll from '~/vue/directives/infiniteScroll';
import hotkey from '~/vue/directives/hotkey';
import observeVisibility from '~/vue/directives/observeVisibility';

import VueMeta from '~/vue/plugins/vueMeta';
import VueLoadingSpinner from '~/vue/plugins/vueLoadingSpinner';
import VueSanitize from '~/vue/plugins/vueSanitize';
import { VueNotifications } from '~/vue/plugins/notifications';
import { GlobalHelpersPlugin } from '~/vue/filters/globalHelpers';
import uiv from '~/vue/wrapped_libraries/Uiv';

import Switch from '~/vue/common_components/Switch.vue';
import SwitchWithLabel from '~/vue/common_components/SwitchWithLabel.vue';
import SimpleHeader from '~/vue/common_components/SimpleHeader.vue';
import FolderButton from '~/vue/common_components/FolderButton/FolderButton.vue';
import FolderButtonEditToggle from '~/vue/common_components/FolderButton/FolderButtonEditToggle.vue';
import StuffPreview from '~/vue/common_components/StuffPreview.vue';
import VueSearchInput from '~/vue/common_components/SearchInput.vue';
import AudioToggle from '~/vue/common_components/AudioToggle.vue';
import FolderItem from '~/vue/common_components/FolderItem.vue';
import SimpleLink from '~/vue/common_components/SimpleLink.vue';
import SimpleLinkWithAvatar from '~/vue/common_components/SimpleLinkWithAvatar.vue';
import MediaPreview from '~/vue/common_components/MediaPreview.vue';
import LocationWithMarker from '~/vue/common_components/LocationWithMarker.vue';
import MediaPreviewAddRequest from '~/vue/common_components/MediaPreviewAddRequest.vue';
import InvitationTypeSelectPopup from '~/vue/common_components/invitations/InvitationTypeSelectPopup.vue';
import EmailRoleCategoryPopup from '~/vue/common_components/invitations/EmailRoleCategoryPopup.vue';
import PageAlternativeHeader from '~/vue/common_components/PageAlternativeHeader';
import GoToManageInvitesPageProposalPopup
    from '~/vue/common_components/invitations/GoToManageInvitesPageProposalPopup.vue';
import RenderQuestion from '~/angular/app/blogolog/render_question/RenderQuestion.vue';

// the components a template can name without importing it. Everything else is a local
// `components: {...}` - this list is the one every section was allowed to assume
const COMPONENTS = {
    VueSwitch: Switch,
    VueSwitchWithLabel: SwitchWithLabel,
    VueSimpleHeader: SimpleHeader,
    VueFolderButton: FolderButton,
    VueFolderButtonEditToggle: FolderButtonEditToggle,
    InvitationTypeSelectPopup,
    EmailRoleCategoryPopup,
    VueStuffPreview: StuffPreview,
    VueSearchInput,
    AudioToggle,
    FolderItem,
    SimpleLink,
    RenderQuestion,
    SimpleLinkWithAvatar,
    MediaPreview,
    MediaPreviewAddRequest,
    LocationWithMarker,
    GoToManageInvitesPageProposalPopup,
    PageAlternativeHeader,
};

const DIRECTIVES = {
    'infinite-scroll': infiniteScroll,
    hotkey,
    'observe-visibility': observeVisibility,
};

const GlobalRegistrationsPlugin = {
    install (app) {
        // pinia and vue-router are not here: they are per app in vue/createApp.js and
        // spa/mount, which is what pinia and vue-router 4 require
        app.use(VueMeta);
        app.use(uiv);
        app.use(Vue3TouchEvents, {
            disableClick: true,
            swipeTolerance: 100,
        });

        // `$customTerm`, `$cutName`, `$backHrefQuery` and the rest - what the global
        // filters became - plus `$notifications`, `$loadingSpinner` and `$sanitize`
        app.use(GlobalHelpersPlugin);
        app.use(VueNotifications);
        app.use(VueLoadingSpinner);
        app.use(VueSanitize);

        for (const [name, component] of Object.entries(COMPONENTS)) {
            app.component(name, component);
        }
        for (const [name, directive] of Object.entries(DIRECTIVES)) {
            app.directive(name, directive);
        }
    },
};

export default GlobalRegistrationsPlugin;
export { GlobalRegistrationsPlugin, COMPONENTS, DIRECTIVES };
